/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { differenceInDays } from "date-fns";

import { Card, Divider, Tab, Tabs, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Datagrid,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { ChevronRightIcon } from "../../../components/common/iconography";
import ExportButton from "../../../components/common/ExportButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { YardDateField } from "../../../components/common/YardDateField";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { InboundStatus } from "../../../utils/constants";
import { exceptionRenderer, normalizeValue } from "../../../utils/utils";
import { InboundStateIcon } from "../../../managerApp/resources/inboundShipments/InboundStateIcon";
import { useUser } from "../../../authenticatorApp/UserContext";
import { exporter } from "./InboundList";

/**
 * Customer portal SIT (storage in transit) cars — read-only counterpart of the
 * managerApp SIT List. SIT_PENDING / SIT_PLACED shipments never match the
 * Inbound Shipments tabs (each tab is one exact status on the GSI), so without
 * this page a customer cannot see SIT cars at all.
 */

const useStyles = makeStyles((theme) => ({
  listRoot: {
    transition: theme.transitions.create(["all"], {
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const tabs = [
  {
    id: InboundStatus.SIT_PENDING,
    name: "Pending",
    sortKey: "scheduledDateTime",
    sortOrder: "ASC",
  },
  {
    id: InboundStatus.SIT_PLACED,
    name: "PLACED",
    sortKey: "placedDateTime",
    sortOrder: "ASC",
  },
];

// SIT age = whole days the car has been placed (based on placedDateTime).
const sitAgeRenderer = (record) =>
  record?.placedDateTime
    ? differenceInDays(new Date(), new Date(record.placedDateTime))
    : "";

const SITSearchField = (props) => {
  const { label, alwaysOn } = props;
  const { displayedFilters, filterValues, setFilters } = useListContext(props);

  const handleSearch = (value) => {
    const nextFilterValues = { ...filterValues };
    delete nextFilterValues.or;
    if (value) {
      const normalizedValue = normalizeValue(value);
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { carrierName: { matchPhrasePrefix: `${value}` } },
            { normalizedPoNumber: { wildcard: `*${normalizedValue}*` } },
            { normalizedRailCarNumber: { wildcard: `*${normalizedValue}*` } },
            { customFields: { matchPhrasePrefix: `${value}` } },
            { customFields: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters,
      );
    } else {
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const listFilters = [<SITSearchField label="Search" alwaysOn />];

const ListActions = (props) => {
  const { xsDown } = props;
  const { resource, total } = useListContext();

  return !xsDown ? (
    <TopToolbar>
      <FilterButton />
      <ExportButton
        disabled={total === 0}
        resource={resource}
        exporter={exporter}
      />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

const getSortBy = (tabIndex) =>
  `{"key":"inboundShipmentByAccountByStatusByDates", "search":"${tabs[tabIndex].sortKey}"}`;

const getDefaultFilter = (tabIndex, accountId) => ({
  accountId,
  statusReleaseDateTimePlacedDateTimeNotifyDateTimeScheduledDateTimeCreateDateTime:
    {
      beginsWith: {
        status: tabs[tabIndex].id,
      },
    },
});

const getDefaultSearchFilter = (tabIndex, appUser) => ({
  reloadYardId: { eq: appUser?.reloadYardId },
  accountId: { eq: appUser?.accountId },
  status: { eq: tabs[tabIndex].id },
});

const getSessionTab = (appUser) => {
  const sessionTab = Number(sessionStorage.getItem(`SITTab_${appUser?.id}`));
  return sessionTab && tabs[sessionTab] ? sessionTab : 0;
};

export const SITList = (props) => {
  const { appUser } = useUser();
  const classes = useStyles();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [tabIndex, setTabIndex] = useState(getSessionTab(appUser));

  const [defaultFilter, setDefaultFilter] = useState(() =>
    getDefaultFilter(tabIndex, appUser?.accountId),
  );
  const [defaultSearchFilter, setDefaultSearchFilter] = useState(() =>
    getDefaultSearchFilter(tabIndex, appUser),
  );
  const defaultSort = {
    field: getSortBy(tabIndex),
    order: tabs[tabIndex].sortOrder,
  };

  // memorize perPage settings
  const sessionPerPage = Number(
    localStorage.getItem(`${props.resource}_perPage`),
  );
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? sessionPerPage : 10,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "SIT",
    defaultFilter: defaultFilter,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    defaultSort: defaultSort,
    resource: "inboundShipments",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      const keyToCheck = JSON.parse(defaultSort.field).key;
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
  }, [listContext?.filter]);

  const handleTabChange = (value) => {
    setTabIndex(value);
    setDefaultFilter(getDefaultFilter(value, appUser?.accountId));
    setDefaultSearchFilter(getDefaultSearchFilter(value, appUser));
    listContext.setSort(getSortBy(value), tabs[value].sortOrder);
    listContext.setPage(1);
  };

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="SIT Cars" />} />
      <ListToolbar
        {...props}
        filters={listFilters}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card className={classes.listRoot}>
        <SITTabs
          {...props}
          {...listContext}
          appUser={appUser}
          tabIndex={tabIndex}
          setTabIndex={handleTabChange}
          xsDown={xsDown}
          isMedium={mdDown}
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const SITTabs = ({ tabIndex, setTabIndex, setPage, appUser, ...props }) => {
  const handleChange = (event, value) => {
    sessionStorage.setItem(`SITTab_${appUser?.id}`, value);
    setTabIndex(value);
  };

  return (
    <>
      <Tabs
        value={Number(tabIndex)}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleChange}
      >
        {tabs.map((tab, idx) => (
          <Tab key={tab.id} label={tab.name} id={`sit-${idx}`} />
        ))}
      </Tabs>
      <Divider />
      <SITGrid {...props} tabIndex={tabIndex} tabValue={tabs[tabIndex].id} />
    </>
  );
};

const SITGrid = ({ ids, page, tabIndex, tabValue, ...props }) => {
  const { xsDown, isMedium } = props;
  const isPlaced = tabValue === InboundStatus.SIT_PLACED;

  if (xsDown)
    return (
      <SimpleList
        linkType="show"
        primaryText={(record) => `Car/Trl #:: ${record.railCarNumber}`}
        secondaryText={(record) => `PO #: ${record.poNumber}`}
        tertiaryText={(record) =>
          isPlaced ? `${sitAgeRenderer(record)} days` : ""
        }
        leftIcon={(record) => <InboundStateIcon record={record} />}
        rightIcon={() => (
          <ChevronRightIcon
            fontSize="large"
            color="secondary"
            style={{ marginLeft: 40, marginTop: 10 }}
          />
        )}
      />
    );

  return (
    <Datagrid rowClick="show" hasBulkActions={false}>
      {isPlaced ? <InboundStateIcon /> : null}
      {isPlaced ? (
        <FunctionField
          source="hasException"
          label=" "
          render={exceptionRenderer}
        />
      ) : null}
      {isPlaced ? (
        <YardDateField
          source="placedDateTime"
          label="Placed Date"
          sortable={true}
          sortBy={getSortBy(tabIndex)}
        />
      ) : (
        <YardDateField
          source="scheduledDateTime"
          label="Scheduled Time"
          showTime={true}
          sortable={true}
          sortBy={getSortBy(tabIndex)}
        />
      )}
      <TextField source="railCarNumber" label="Car/Trl No" sortable={true} />
      <TextField source="poNumber" label="PO Number" sortable={true} />
      {isPlaced ? (
        <FunctionField
          source="placedDateTime"
          label="Age (days)"
          sortable={false}
          render={sitAgeRenderer}
        />
      ) : null}
      {!isMedium ? <ShowButton /> : null}
    </Datagrid>
  );
};
